/**
 * The working location.
 *
 * One selection, shared by every screen: what the till sells from, what the
 * receiving note receives into, which stock a count walks, which expenses a
 * list shows. A screen that resolved its own location would be a screen that
 * disagrees with the one beside it.
 *
 * Two things this is NOT.
 *
 * It is not access control. The API returns only the locations the caller may
 * work in, and row level security enforces that on every query regardless of
 * what this context says. Choosing a location here cannot grant one — pointing
 * the selector at somewhere forbidden produces an empty screen, not a leak.
 *
 * It is also the geographic scope of every figure: Country → State/Region →
 * Location, chosen once in the sidebar. A list, a total, a chart and an export
 * on any screen read through the same selection (`useLocationFilter`), and the
 * API narrows the request to it — so two screens cannot disagree about what
 * "Enugu" covers. "All" at any step means "do not narrow at this step".
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useAuth, useBusinessPath } from './auth';
import {
  EVERYWHERE,
  countryOptions,
  locationsIn,
  normaliseSelection,
  regionOptions,
  regionTermOf,
  scopeParams,
  withCountry,
  withRegion,
  type GeoSelection,
  type Option,
  type RegionTerm,
} from './geoScope';

export interface WorkingLocation {
  id: string;
  name: string;
  type: 'physical' | 'external_mobile';
  isDefault: boolean;
  /** Resolved country; optional so an older API response still parses. */
  countryCode?: string;
  /** Resolved state (the location's, else the business's in its country). Null: none. */
  stateCode?: string | null;
  currencyCode?: string;
  timezone?: string;
}

interface MyLocations {
  canSeeAll: boolean;
  defaultBranchId: string | null;
  branches: WorkingLocation[];
}

interface LocationContextValue {
  /** The locations this login may work in. Already filtered by the API. */
  locations: WorkingLocation[];
  /** Whether "All locations" is on offer — only for an unrestricted member. */
  canSeeAll: boolean;
  /**
   * Whether this login may look at more than one location at once — a whole
   * country, a state, or every location it holds. True for an unrestricted
   * member and for one assigned several locations; never for a location login.
   * The API narrows every read to the caller's own locations regardless.
   */
  canAggregate: boolean;
  /** `null` means no single location: an area, or everything. */
  branchId: string | null;
  /** The whole Country → State/Region → Location selection. */
  selection: GeoSelection;
  /** The countries this login has a location in. */
  countries: Option[];
  /** The divisions of the selected country; empty when it has none. */
  regions: Option[];
  /**
   * The country `regions` belongs to: the selected one, or — when this login
   * has locations in a single country — that country, so its states are on
   * offer straight away rather than behind a one-option choice.
   */
  regionCountry: string | null;
  /** The locations inside the selected country and region. */
  scopedLocations: WorkingLocation[];
  /** How the selected country names its divisions ("State", "County"…). */
  regionTerm: RegionTerm;
  /**
   * The business's own default location, as the API reports it.
   *
   * Reads may span every location; a WRITE cannot — a sale happened somewhere.
   * This is what `useRecordLocation` falls back to when the switcher is showing
   * "All locations", so recording something does not require the user to first
   * narrow the whole app.
   */
  defaultBranchId: string | null;
  location: WorkingLocation | null;
  /** Pick one location (its country and region follow), or null for "All locations". */
  setBranchId: (next: string | null) => void;
  /** Pick a country, or null for all; the region and location reset. */
  setCountry: (next: string | null) => void;
  /** Pick a region of the selected country, or null for all; an outside location resets. */
  setRegion: (next: string | null) => void;
  isLoading: boolean;
  /**
   * Whether the working location has been settled.
   *
   * False for the first moment of a session, while the permitted list is in
   * flight. A list screen that fetches before this is true asks for the whole
   * business and then re-asks for one location — the owner sees every location's
   * records flash past before the answer they wanted. Gating on this is the
   * difference between a screen that is briefly wrong and one that is briefly
   * empty, and briefly empty is honest.
   */
  isReady: boolean;
}

const LocationCtx = createContext<LocationContextValue | null>(null);

/**
 * Per person and per business: switching business does not carry a selection
 * across, and two people sharing a browser do not inherit each other's.
 */
const storageKey = (userId: string, businessId: string): string => `cenox.scope.${userId}.${businessId}`;
/** Where a selection lived before the hierarchy: one location id, or 'all'. */
const legacyKey = (businessId: string): string => `cenox.location.${businessId}`;

function remembered(userId: string, businessId: string): GeoSelection | undefined {
  try {
    const raw = window.localStorage.getItem(storageKey(userId, businessId));
    if (raw !== null) {
      const v = JSON.parse(raw) as Partial<GeoSelection>;
      const str = (x: unknown): string | null => (typeof x === 'string' && x ? x : null);
      return { country: str(v.country), region: str(v.region), branchId: str(v.branchId) };
    }
    const legacy = window.localStorage.getItem(legacyKey(businessId));
    if (legacy === null) return undefined;
    return legacy === 'all' ? EVERYWHERE : { ...EVERYWHERE, branchId: legacy };
  } catch {
    // A browser with site data blocked still has to work; it just forgets.
    return undefined;
  }
}

function remember(userId: string, businessId: string, sel: GeoSelection): void {
  try {
    window.localStorage.setItem(storageKey(userId, businessId), JSON.stringify(sel));
  } catch {
    // Remembering is a convenience; failing to is not an error.
  }
}

const sameSelection = (a: GeoSelection, b: GeoSelection): boolean =>
  a.country === b.country && a.region === b.region && a.branchId === b.branchId;

export function LocationProvider({ children }: { children: ReactNode }): ReactNode {
  const { business, user } = useAuth();
  const path = useBusinessPath();
  const businessId = business?.businessId ?? null;
  const userId = user?.id ?? 'anonymous';
  const isOwner = business?.role === 'owner';
  const isCounter = business?.role === 'location_operator';
  const businessCountry = business?.countryCode ?? null;

  const [chosen, setChosen] = useState<GeoSelection | undefined>(undefined);

  const mine = useQuery({
    queryKey: ['my-locations', businessId],
    queryFn: () => api.get<MyLocations>(path('/branches/mine')),
    enabled: Boolean(businessId),
    staleTime: 60_000,
  });

  const locations = useMemo(() => mine.data?.branches ?? [], [mine.data]);
  const canSeeAll = mine.data?.canSeeAll ?? false;
  const canAggregate = canSeeAll || (!isCounter && locations.length > 1);

  // A new business means a new selection; the old one means nothing here.
  useEffect(() => {
    setChosen(undefined);
  }, [businessId, userId]);

  /**
   * Settle on a starting selection once the permitted list arrives, and hold
   * it to that list every time the list changes.
   *
   * Remembered choice first, but only what of it is still permitted — access
   * can be narrowed between sessions, and restoring a location somebody may no
   * longer read would open an empty screen they cannot explain. Each step that
   * no longer resolves falls back to "All" at that step.
   *
   * With nothing remembered, an owner who may see every location starts on
   * everything. Starting them on the business default made the dashboard show
   * one location's figures as though they were the business's, with nothing on
   * screen to say so. Everyone else gets the server's answer: the business
   * default when visible, their own first location when not.
   */
  useEffect(() => {
    if (!businessId || !mine.data) return;
    const fallback = mine.data.defaultBranchId;

    if (chosen === undefined) {
      const saved = remembered(userId, businessId);
      const start: GeoSelection =
        saved ?? (isOwner && canSeeAll ? EVERYWHERE : { ...EVERYWHERE, branchId: fallback });
      setChosen(normaliseSelection(start, locations, businessCountry, canAggregate, fallback));
      return;
    }

    // The permitted list changed under a settled selection: re-validate it.
    const held = normaliseSelection(chosen, locations, businessCountry, canAggregate, fallback);
    if (!sameSelection(held, chosen)) setChosen(held);
  }, [businessId, userId, isOwner, canSeeAll, canAggregate, mine.data, locations, businessCountry, chosen]);

  const commit = useCallback(
    (next: GeoSelection) => {
      setChosen(next);
      if (businessId) remember(userId, businessId, next);
    },
    [businessId, userId],
  );

  const current = chosen ?? EVERYWHERE;

  const setBranchId = useCallback(
    (next: string | null) => {
      if (next === null) {
        commit({ ...current, branchId: null });
        return;
      }
      commit(normaliseSelection({ ...EVERYWHERE, branchId: next }, locations, businessCountry, canAggregate, null));
    },
    [commit, current, locations, businessCountry, canAggregate],
  );
  const setCountry = useCallback((next: string | null) => commit(withCountry(current, next)), [commit, current]);
  const onlyCountry = useMemo(() => {
    const c = countryOptions(locations, businessCountry);
    return c.length === 1 ? c[0]!.value : null;
  }, [locations, businessCountry]);
  const setRegion = useCallback(
    (next: string | null) => {
      // A state picked before a country, with only one country on offer, is in it.
      const base = current.country || next === null || !onlyCountry ? current : { ...current, country: onlyCountry };
      commit(withRegion(base, next, locations, businessCountry));
    },
    [commit, current, locations, businessCountry, onlyCountry],
  );

  const value = useMemo<LocationContextValue>(() => {
    const branchId = current.branchId;
    const countries = countryOptions(locations, businessCountry);
    const regionCountry = current.country ?? onlyCountry;
    const regions = regionOptions(locations, businessCountry, regionCountry);
    const scopedLocations = locationsIn(locations, businessCountry, current.country, current.region) as WorkingLocation[];
    return {
      locations,
      canSeeAll,
      canAggregate,
      branchId,
      selection: current,
      countries,
      regions,
      regionCountry,
      scopedLocations,
      regionTerm: regionTermOf(regionCountry ?? businessCountry),
      defaultBranchId: mine.data?.defaultBranchId ?? null,
      location: locations.find((b) => b.id === branchId) ?? null,
      setBranchId,
      setCountry,
      setRegion,
      isLoading: mine.isPending,
      // Settled once the permitted list has arrived AND a choice has been made
      // from it — `chosen` stays undefined until the effect above resolves one.
      isReady: Boolean(mine.data) && chosen !== undefined,
    };
  }, [locations, canSeeAll, canAggregate, current, businessCountry, onlyCountry, mine.data, mine.isPending, chosen, setBranchId, setCountry, setRegion]);

  return <LocationCtx.Provider value={value}>{children}</LocationCtx.Provider>;
}

/**
 * The working location, for any screen that records or lists something.
 *
 * Safe outside the provider — it reports "all locations, none permitted",
 * which renders an ordinary empty state rather than crashing a screen that
 * happens to be mounted in a test or a route above the provider.
 */
export function useWorkingLocation(): LocationContextValue {
  return (
    useContext(LocationCtx) ?? {
      locations: [],
      canSeeAll: false,
      canAggregate: false,
      branchId: null,
      selection: EVERYWHERE,
      countries: [],
      regions: [],
      regionCountry: null,
      scopedLocations: [],
      regionTerm: regionTermOf(null),
      defaultBranchId: null,
      location: null,
      setBranchId: () => undefined,
      setCountry: () => undefined,
      setRegion: () => undefined,
      isLoading: false,
      // Outside the provider there is nothing to wait for.
      isReady: true,
    }
  );
}

/**
 * The query fragment a list screen appends, and the body field a write sends.
 *
 * Returns an empty object for "all locations" so callers can spread it
 * unconditionally — `{...locationFilter()}` reads better at every call site
 * than an `if` around each one, and an omitted filter is exactly what "do not
 * narrow" means.
 */
/** The geographic scope a read sends: one location, or an area, or nothing. */
export type ScopeFilter = { branchId?: string; scopeCountry?: string; scopeState?: string };

export function useLocationFilter(): ScopeFilter {
  const { selection } = useWorkingLocation();
  return useMemo(() => scopeParams(selection), [selection]);
}

/**
 * The same scope in the `country` / `state` / `branchId` vocabulary of the
 * endpoints that filtered by geography before the sidebar did (the remittance
 * sheet, rider earnings). Sent alongside `useLocationFilter`'s parameters so
 * those endpoints' own filters and the request-wide scope always agree.
 */
export function useAreaFilter(): { branchId?: string; country?: string; state?: string } {
  const { selection } = useWorkingLocation();
  return useMemo(() => {
    const out: { branchId?: string; country?: string; state?: string } = {};
    if (selection.branchId) out.branchId = selection.branchId;
    if (selection.country) out.country = selection.country;
    if (selection.country && selection.region) out.state = selection.region;
    return out;
  }, [selection]);
}

/**
 * Whether a location-aware list may fetch yet.
 *
 * Pair with the page's own `enabled`: fetching before the working location is
 * settled asks for the whole business and then re-asks for one location, and the
 * user watches the wrong answer go past.
 */
export function useLocationReady(): boolean {
  return useWorkingLocation().isReady;
}

/**
 * The location a NEW record belongs to.
 *
 * Reads can honestly span the whole business; a write cannot — a sale happened
 * in a place, stock arrived somewhere, cash was counted at a till. The API now
 * refuses an operational write with no location rather than filing it at the
 * business default, so every form that records something resolves one here:
 *
 *  1. whatever the switcher is pointed at, because that is what the user
 *     believes they are working in;
 *  2. failing that ("All locations"), the business's default location, so an
 *     owner recording a sale is not made to narrow the entire app first;
 *  3. failing that, the single location they have, if there is only one.
 *
 * When none of those resolve, `unresolved` is true and the form says so instead
 * of posting something the API will reject. The returned value is local state:
 * a form with more than one location on offer lets the user correct it for that
 * one record without moving the whole app's selection.
 */
export function useRecordLocation(): {
  /** Empty string when nothing could be resolved, so it binds to a `<select>`. */
  branchId: string;
  setBranchId: (next: string) => void;
  /** The locations this login may record into. */
  locations: WorkingLocation[];
  /** True when there is no location to record into — the form must not submit. */
  unresolved: boolean;
} {
  const { branchId: working, defaultBranchId, locations } = useWorkingLocation();

  const resolved = useMemo(() => {
    const permitted = new Set(locations.map((b) => b.id));
    if (working && permitted.has(working)) return working;
    if (defaultBranchId && permitted.has(defaultBranchId)) return defaultBranchId;
    if (locations.length === 1) return locations[0]?.id ?? '';
    return '';
  }, [working, defaultBranchId, locations]);

  /**
   * `null` means "the user has not overridden it", so a later change to the
   * switcher still moves the form. An explicit choice sticks.
   */
  const [override, setOverride] = useState<string | null>(null);
  const branchId = override ?? resolved;

  return {
    branchId,
    setBranchId: setOverride,
    locations,
    unresolved: !branchId,
  };
}
